import { EtfEyebrow } from "@/components/layout/EtfEyebrow";
import { Seo } from "@/lib/seo";
import { Link, useLocation } from "react-router-dom";
import type { LegalPageContent } from "../../data/legal";

// LegalPageRenderer
// Ported from dane/frontend/app/etf-website/components/legal-page.tsx.
// Three sections: hero (title + effective date + entity badge),
// sticky tab nav (3 tabs), and a two-column body (sticky TOC sidebar
// + sectioned content with orange-eyebrow + horizontal rule).
//
// `htmlContent` is our own static copy from data/legal.ts (not user input)
// so `dangerouslySetInnerHTML` is safe here. Source code applies the same
// pattern for both `intro` and per-section content.

interface LegalPageRendererProps {
  content: LegalPageContent;
}

export function LegalPageRenderer({ content }: LegalPageRendererProps) {
  const { hero, intro, tabs, sections } = content;
  const { pathname } = useLocation();

  return (
    <div className="min-h-screen">
      <Seo
        title={hero.title}
        description={`${hero.title} for Corgi Funds${hero.effectiveDate ? ` — effective ${hero.effectiveDate}` : ""}.`}
        canonicalPath={pathname}
      />
      <LegalHero hero={hero} />
      <LegalTabNav tabs={tabs} currentPath={pathname} />

      <div className="flex flex-col lg:flex-row gap-0 px-6 md:px-10 pt-10 pb-[60px] md:pt-16 md:pb-20">
        <LegalTocSidebar sections={sections} />

        <div className="flex-1 min-w-0 max-w-[760px]">
          {intro && <LegalIntro intro={intro} effectiveDate={hero.effectiveDate} />}
          {sections.map((section) => (
            <LegalSectionBlock
              key={section.id}
              id={section.id}
              title={section.bodyTitle ?? section.title}
              htmlContent={section.htmlContent}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────

function LegalHero({ hero }: { hero: LegalPageContent["hero"] }) {
  const titleWords = hero.title.split(" ");
  return (
    <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-end px-6 md:px-10 pt-12 pb-10 md:pt-[72px] md:pb-[60px] border-b border-[var(--etf-border)]">
      <div>
        <EtfEyebrow className="mb-5">Legal</EtfEyebrow>
        <h1 className="font-[family-name:var(--font-f37bolton)] text-[clamp(56px,7vw,96px)] font-normal leading-none tracking-tight text-[var(--etf-white)]">
          {titleWords.map((word, i) => {
            const isLast = i === titleWords.length - 1;
            // Legal page titles are short, unique-token phrases ("Privacy
            // Policy", "Terms of Use", "Social Media Disclosures") so the
            // word itself is a stable, unique key without needing the index.
            if (isLast) {
              return (
                <em key={word} className="not-italic text-[var(--etf-orange)]">
                  {word}
                </em>
              );
            }
            return <span key={word}>{word} </span>;
          })}
        </h1>
      </div>
      <div>
        <p className="text-base italic font-light text-[var(--etf-white2)] leading-[1.75] mb-7">
          Privacy policy, terms of use, and social media disclosures governing your interaction with
          Corgi Funds and its affiliated entities.
        </p>
        <div className="flex gap-8">
          <LegalHeroFact label="Effective" value={hero.effectiveDate} />
          <LegalHeroFact label="Entity" value={hero.entityBadge} />
        </div>
      </div>
    </section>
  );
}

function LegalHeroFact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="font-[family-name:var(--font-instrument)] text-[13px] text-[var(--etf-white)] tracking-[0.02em]">
        {value}
      </div>
      <div className="font-[family-name:var(--font-instrument)] text-[11px] text-[var(--etf-white3)] tracking-widest uppercase mt-0.5">
        {label}
      </div>
    </div>
  );
}

function LegalTabNav({
  tabs,
  currentPath,
}: {
  tabs: LegalPageContent["tabs"];
  currentPath: string;
}) {
  return (
    <div className="sticky top-14 z-[100] flex px-6 md:px-10 border-b border-[var(--etf-border)] bg-[var(--etf-black2)] overflow-x-auto">
      {tabs.map((tab, i) => {
        const isActive = currentPath === tab.href;
        const baseClasses =
          "font-[family-name:var(--font-instrument)] text-sm tracking-[0.06em] no-underline py-[18px] px-6 border-b-2 whitespace-nowrap transition-all";
        const activeClasses = isActive
          ? "text-[var(--etf-white)] border-[var(--etf-orange)]"
          : "text-[var(--etf-white3)] border-transparent hover:text-[var(--etf-white2)]";
        const firstClass = i === 0 ? "pl-0" : "";
        return (
          <Link
            key={tab.id}
            to={tab.href}
            className={`${baseClasses} ${activeClasses} ${firstClass}`}
          >
            {tab.label}
          </Link>
        );
      })}
    </div>
  );
}

function LegalTocSidebar({
  sections,
}: {
  sections: LegalPageContent["sections"];
}) {
  return (
    <aside className="w-full lg:w-[260px] flex-shrink-0 lg:sticky lg:top-[130px] lg:self-start lg:pr-10 mb-10 lg:mb-0">
      <div className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.18em] uppercase text-[var(--etf-white3)] mb-4">
        On this page
      </div>
      <div className="flex flex-col gap-1.5">
        {sections.map((section) => (
          <a
            key={section.id}
            href={`#${section.id}`}
            className="font-[family-name:var(--font-instrument)] text-[13px] text-[var(--etf-white3)] no-underline tracking-[0.02em] py-[5px] hover:text-[var(--etf-white2)] transition-colors border-l border-l-[var(--etf-border)] pl-[13px]"
          >
            {section.title}
          </a>
        ))}
      </div>
    </aside>
  );
}

function LegalIntro({
  intro,
  effectiveDate,
}: {
  intro: string;
  effectiveDate: string;
}) {
  return (
    <>
      <div className="font-[family-name:var(--font-instrument)] bg-[var(--etf-black2)] border border-[var(--etf-border)] border-l-[3px] border-l-[var(--etf-orange)] px-[19px] py-[13px] text-xs text-[var(--etf-white3)] mb-8">
        Effective as of {effectiveDate} — Corgi Strategies, LLC and its affiliate companies
      </div>
      <div
        className="text-[15px] font-light text-[var(--etf-white2)] leading-[1.85] pb-8 mb-10 border-b border-[var(--etf-border)] [&_p+p]:mt-4"
        // biome-ignore lint/security/noDangerouslySetInnerHtml: legal content is static, author-controlled (data/legal.ts)
        dangerouslySetInnerHTML={{ __html: intro }}
      />
    </>
  );
}

function LegalSectionBlock({
  id,
  title,
  htmlContent,
}: {
  id: string;
  title: string;
  htmlContent: string;
}) {
  return (
    <div id={id} className="mb-10 scroll-mt-[130px]">
      <div className="flex items-center gap-2.5 mb-3.5">
        <span className="font-[family-name:var(--font-instrument)] text-xs font-bold tracking-[0.16em] uppercase text-[var(--etf-orange)] whitespace-nowrap">
          {title}
        </span>
        <div className="flex-1 h-px bg-[var(--etf-border)]" />
      </div>
      <div
        className="text-[15px] font-light text-[var(--etf-white2)] leading-[1.85] [&_p+p]:mt-4"
        // biome-ignore lint/security/noDangerouslySetInnerHtml: legal content is static, author-controlled (data/legal.ts)
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    </div>
  );
}
